import { useRef, useCallback, useMemo, useEffect, memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-hot-toast'
import { useTransactionStore } from '@/store/useTransactionStore'

interface PaymentIframeProps {
  paymentUrl: string
  transactionId: string | null
}

export const PaymentIframe = memo(function PaymentIframe({
  paymentUrl,
  transactionId,
}: PaymentIframeProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const clearLastTransactionData = useTransactionStore(
    (state) => state.clearLastTransactionData,
  )

  const title = useMemo(() => t('common:payment'), [t])

  const iframeStyle = useMemo(
    () => ({
      width: '100%',
      height: '100vh',
      minHeight: '500px',
      border: 'none',
    }),
    [],
  )

  const handleIframeLoad = useCallback(() => {
    console.log('[PaymentIframe] Payment iframe loaded')
  }, [])

  const handleIframeError = useCallback(() => {
    console.error('[PaymentIframe] Failed to load payment iframe')
    toast.error(t('common:failedToLoadPaymentPage'))
  }, [t])

  const handleWebViewMessage = useCallback(
    (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data)

        if (data.type === 'PAYMENT_COMPLETED') {
          toast.success(t('common:paymentCompletedSuccessfully'))
          clearLastTransactionData()
          navigate('/transaction-success', {
            state: {
              title: t('common:paymentSuccessful'),
              description: t('common:yourPaymentHasBeenProcessed'),
              transactionId: transactionId,
            },
          })
        } else if (data.type === 'PAYMENT_FAILED') {
          toast.error(t('common:paymentFailed'))
        } else if (data.type === 'PAYMENT_CANCELLED') {
          toast.error(t('common:paymentCancelled'))
          clearLastTransactionData()
          navigate('/activity')
        }
      } catch (error) {
        // Non-JSON message, ignoring
      }
    },
    [clearLastTransactionData, navigate, transactionId, t],
  )

  useEffect(() => {
    window.addEventListener('message', handleWebViewMessage)
    return () => {
      window.removeEventListener('message', handleWebViewMessage)
    }
  }, [handleWebViewMessage])

  return (
    <iframe
      ref={iframeRef}
      key={paymentUrl}
      src={paymentUrl}
      style={iframeStyle}
      title={title}
      // sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-top-navigation allow-popups-to-escape-sandbox"
      allow="payment"
      onLoad={handleIframeLoad}
      onError={handleIframeError}
    />
  )
})
